import { useEffect, useRef } from 'react';

// How often an open tab re-checks for a newer deployed bundle. Also checks when
// the tab regains visibility, which is when long-lived tabs actually come back.
export const VERSION_POLL_INTERVAL_MS = 15 * 60 * 1000;

// Reads the deployed build id emitted next to the bundle at build time.
// Returns null when unavailable: the dev server has no version.json, and Caddy
// rewrites unknown paths to index.html — whose HTML fails JSON.parse — so the
// check silently disables itself instead of ever alarming on bad data.
async function fetchDeployedSha(): Promise<string | null> {
  try {
    const response = await fetch('/version.json', { cache: 'no-store' });
    if (!response.ok) return null;
    const data: unknown = await response.json();
    const sha = (data as { sha?: unknown } | null)?.sha;
    return typeof sha === 'string' && sha !== '' && sha !== 'dev' ? sha : null;
  } catch {
    return null;
  }
}

// Fires onNewVersion exactly once when the server starts answering with a
// different build id than the one this tab first saw. An SPA tab otherwise
// keeps its loaded bundle forever — which is how an already-fixed error banner
// kept rendering raw JSON from a tab opened before the fix deployed.
export function useVersionCheck(onNewVersion: () => void) {
  const callbackRef = useRef(onNewVersion);
  callbackRef.current = onNewVersion;

  useEffect(() => {
    let bootSha: string | null = null;
    let notified = false;
    let disposed = false;

    const check = async () => {
      if (notified) return;
      const sha = await fetchDeployedSha();
      if (disposed || notified || sha === null) return;
      if (bootSha === null) {
        // First successful read is the baseline, even if it happens long after
        // mount (e.g. the tab was offline when it booted).
        bootSha = sha;
        return;
      }
      if (sha !== bootSha) {
        notified = true;
        callbackRef.current();
      }
    };

    void check();
    const interval = window.setInterval(() => void check(), VERSION_POLL_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      disposed = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);
}
